import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaCamera, FaMapMarkerAlt, FaSignOutAlt, FaUser } from "react-icons/fa";
import Swal from "sweetalert2";
import Loading from "../../component/Loading";
import { removeLocalStorage } from "../../helper/auth";
import AuthImage from "../../component/common/AuthImage";
import { compressImageFile } from "../../helper/compressImage";

const MAX_SOURCE_BYTES = 15 * 1024 * 1024; // izinkan foto kamera besar, nanti dikompres
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

const Profile = () => {
    const [userData, setUserData] = useState(null);
    const [lokasiList, setLokasiList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [formData, setFormData] = useState({
        nama: "",
        email: "",
        password: "",
        confirmPassword: "",
    });
    const [previewImage, setPreviewImage] = useState(null);
    const [dirty, setDirty] = useState(false);
    const fileInputRef = useRef(null);

    const getData = async () => {
        try {
            setLoading(true);
            const [userRes, lokasiRes] = await Promise.all([
                axios.get(`/api/user`, { withCredentials: true }),
                axios.get(`/api/lokasi/saya`, { withCredentials: true }).catch(() => ({ data: [] })),
            ]);
            const user = userRes.data;
            setUserData(user);
            setFormData({
                nama: user.nama || "",
                email: user.email || "",
                password: "",
                confirmPassword: "",
            });
            setPreviewImage(user.foto_profile || null);
            setLokasiList(Array.isArray(lokasiRes.data) ? lokasiRes.data : []);
            setDirty(false);
        } catch (error) {
            console.error("Error fetching user data:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Gagal memuat data profile",
                confirmButtonColor: "#1B3C33",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setDirty(true);
    };

    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic"].includes(file.type)
            && !file.type.startsWith("image/")) {
            Swal.fire({
                icon: "error",
                title: "Format tidak valid",
                text: "Hanya file gambar",
                confirmButtonColor: "#1B3C33",
            });
            e.target.value = "";
            return;
        }
        if (file.size > MAX_SOURCE_BYTES) {
            Swal.fire({
                icon: "error",
                title: "File terlalu besar",
                text: "Maksimal 15MB (akan dikompres otomatis)",
                confirmButtonColor: "#1B3C33",
            });
            e.target.value = "";
            return;
        }

        try {
            setUploadingPhoto(true);

            // Kompres otomatis: max 1024px, JPEG ~0.8
            let uploadFile = await compressImageFile(file, {
                maxWidth: 1024,
                maxHeight: 1024,
                quality: 0.82,
            });
            // Kalau masih kebesaran, kompres lagi lebih agresif
            if (uploadFile.size > MAX_UPLOAD_BYTES) {
                uploadFile = await compressImageFile(file, {
                    maxWidth: 800,
                    maxHeight: 800,
                    quality: 0.7,
                });
            }
            if (uploadFile.size > MAX_UPLOAD_BYTES) {
                uploadFile = await compressImageFile(file, {
                    maxWidth: 640,
                    maxHeight: 640,
                    quality: 0.6,
                });
            }

            setPreviewImage(URL.createObjectURL(uploadFile));

            const submitData = new FormData();
            submitData.append("nama", (formData.nama || userData?.nama || "").trim());
            submitData.append("email", (formData.email || userData?.email || "").trim().toLowerCase());
            submitData.append("foto_profile", uploadFile);

            const response = await axios.put("/api/user/profile", submitData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUserData(response.data);
            if (response.data.foto_profile) setPreviewImage(response.data.foto_profile);
        } catch (error) {
            setPreviewImage(userData?.foto_profile || null);
            Swal.fire({
                icon: "error",
                title: "Gagal menyimpan foto",
                text: error.response?.data?.message || error.message || "Coba lagi",
                confirmButtonColor: "#1B3C33",
            });
        } finally {
            setUploadingPhoto(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password && formData.password !== formData.confirmPassword) {
            Swal.fire({
                icon: "error",
                title: "Password tidak cocok",
                confirmButtonColor: "#1B3C33",
            });
            return;
        }
        if (formData.password && formData.password.length < 6) {
            Swal.fire({
                icon: "error",
                title: "Password minimal 6 karakter",
                confirmButtonColor: "#1B3C33",
            });
            return;
        }

        try {
            setSaving(true);
            const nama = formData.nama.trim().substring(0, 255);
            const email = formData.email.trim().toLowerCase();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                Swal.fire({
                    icon: "error",
                    title: "Email tidak valid",
                    confirmButtonColor: "#1B3C33",
                });
                return;
            }

            const submitData = new FormData();
            submitData.append("nama", nama);
            submitData.append("email", email);
            if (formData.password) submitData.append("password", formData.password);

            const response = await axios.put("/api/user/profile", submitData, {
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" },
            });

            setUserData(response.data);
            setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
            setDirty(false);
            if (response.data.foto_profile) setPreviewImage(response.data.foto_profile);

            Swal.fire({
                icon: "success",
                title: "Tersimpan",
                timer: 1200,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Gagal",
                text: error.response?.data?.message || "Gagal memperbarui profile",
                confirmButtonColor: "#1B3C33",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = async () => {
        const result = await Swal.fire({
            title: "Logout?",
            showCancelButton: true,
            confirmButtonColor: "#1B3C33",
            cancelButtonColor: "#9ca3af",
            confirmButtonText: "Ya",
            cancelButtonText: "Batal",
        });
        if (result.isConfirmed) {
            await removeLocalStorage();
            window.location.href = "/login";
        }
    };

    if (loading) return <Loading />;
    if (!userData) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center text-zafu-muted">
                Data tidak ditemukan
            </div>
        );
    }

    const row =
        "flex items-center justify-between gap-4 border-b border-black/[0.06] px-1 py-3.5 last:border-b-0";
    const label = "shrink-0 w-[5.5rem] text-[13px] text-zafu-muted";
    const field =
        "w-full bg-transparent text-right text-[15px] text-zafu-ink outline-none placeholder:text-zafu-muted/50";
    const fieldMuted = "w-full bg-transparent text-right text-[15px] text-zafu-muted";

    return (
        <div className="mx-auto max-w-lg px-4 pb-28 pt-2 md:px-0 md:pb-10">
            {/* Avatar hero */}
            <div className="mb-8 flex flex-col items-center">
                <button
                    type="button"
                    disabled={uploadingPhoto}
                    onClick={() => fileInputRef.current?.click()}
                    className="group relative"
                    aria-label="Ubah foto profil"
                >
                    <div className="h-[7.25rem] w-[7.25rem] overflow-hidden rounded-full bg-zafu-cream ring-1 ring-black/5">
                        {previewImage ? (
                            previewImage.startsWith("data:") || previewImage.startsWith("blob:") ? (
                                <img src={previewImage} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <AuthImage
                                    kind="profile"
                                    src={previewImage}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            )
                        ) : (
                            <div className="flex h-full w-full items-center justify-center">
                                <FaUser className="text-3xl text-zafu-muted/40" />
                            </div>
                        )}
                    </div>
                    <span className="absolute inset-0 flex items-end justify-center rounded-full bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-0 transition group-hover:opacity-100">
                        <span className="mb-3 flex items-center gap-1 text-[11px] font-medium text-white">
                            <FaCamera className="text-[10px]" />
                            {uploadingPhoto ? "..." : "Ubah"}
                        </span>
                    </span>
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                />
                <h1 className="mt-4 text-center text-[1.35rem] font-semibold tracking-tight text-zafu-ink">
                    {formData.nama || userData.nama}
                </h1>
                <p className="mt-0.5 text-center text-sm text-zafu-muted">
                    @{userData.username}
                    {userData.nama_devisi ? ` · ${userData.nama_devisi}` : ""}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
                {/* Account */}
                <section className="rounded-2xl bg-white px-4 shadow-[0_1px_0_rgba(0,0,0,0.04)] ring-1 ring-black/[0.05]">
                    <div className={row}>
                        <span className={label}>Nama</span>
                        <input
                            name="nama"
                            value={formData.nama}
                            onChange={handleInputChange}
                            required
                            className={field}
                        />
                    </div>
                    <div className={row}>
                        <span className={label}>Email</span>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className={field}
                        />
                    </div>
                    <div className={row}>
                        <span className={label}>Username</span>
                        <span className={fieldMuted}>{userData.username}</span>
                    </div>
                    <div className={row}>
                        <span className={label}>Devisi</span>
                        <span className={fieldMuted}>{userData.nama_devisi || "—"}</span>
                    </div>
                </section>

                {/* Lokasi absensi — section sendiri, read-only */}
                <section className="rounded-2xl bg-white px-4 py-3 shadow-[0_1px_0_rgba(0,0,0,0.04)] ring-1 ring-black/[0.05]">
                    <div className="mb-2 flex items-center justify-between px-1">
                        <p className="text-[13px] font-medium text-zafu-muted">Lokasi absensi</p>
                        <span className="text-[12px] text-zafu-muted/80">{lokasiList.length} lokasi</span>
                    </div>
                    {lokasiList.length === 0 ? (
                        <p className="px-1 py-2 text-[14px] text-zafu-muted">
                            Belum ada lokasi. Hubungi admin.
                        </p>
                    ) : (
                        <ul className="divide-y divide-black/[0.06]">
                            {lokasiList.map((lok) => (
                                <li
                                    key={lok.id_lokasi || lok.nama}
                                    className="flex items-start gap-3 py-3 first:pt-1 last:pb-1"
                                >
                                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zafu-green/10 text-zafu-green">
                                        <FaMapMarkerAlt className="text-xs" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-[15px] font-medium text-zafu-ink">
                                            {lok.nama || "Lokasi"}
                                        </p>
                                        <p className="mt-0.5 text-[12px] text-zafu-muted">
                                            Radius {lok.radius != null ? `${lok.radius} m` : "—"}
                                            {lok.latitude != null && lok.longitude != null
                                                ? ` · ${Number(lok.latitude).toFixed(5)}, ${Number(lok.longitude).toFixed(5)}`
                                                : ""}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* Security */}
                <section className="rounded-2xl bg-white px-4 shadow-[0_1px_0_rgba(0,0,0,0.04)] ring-1 ring-black/[0.05]">
                    <div className={row}>
                        <span className={label}>Password</span>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Baru (opsional)"
                            className={field}
                            autoComplete="new-password"
                        />
                    </div>
                    {formData.password ? (
                        <div className={row}>
                            <span className={label}>Ulangi</span>
                            <input
                                type="password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Konfirmasi"
                                className={field}
                                autoComplete="new-password"
                            />
                        </div>
                    ) : null}
                </section>

                {/* Actions */}
                <div
                    className={`sticky bottom-[5.5rem] z-20 -mx-4 px-4 pt-2 transition md:static md:bottom-auto md:mx-0 md:px-0 ${
                        dirty ? "opacity-100" : "pointer-events-none opacity-0 md:pointer-events-auto md:opacity-100"
                    }`}
                >
                    <button
                        type="submit"
                        disabled={saving || !dirty}
                        className="w-full rounded-2xl bg-zafu-green py-3.5 text-[15px] font-semibold text-white transition enabled:active:scale-[0.98] disabled:bg-zafu-green/35"
                    >
                        {saving ? "Menyimpan…" : "Simpan"}
                    </button>
                </div>
            </form>

            <button
                type="button"
                onClick={handleLogout}
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-[15px] font-medium text-zafu-green ring-1 ring-zafu-green/25 transition hover:bg-zafu-green/5"
            >
                <FaSignOutAlt className="text-sm" />
                Logout
            </button>
        </div>
    );
};

export default Profile;
